import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";

import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import ShortlistCountCard from "components/Screens/Marketplace/Customer/Shortlist/ShortlistCountCard";
import RequestInterviewsPanel from "components/Screens/Marketplace/Customer/Shortlist/RequestInterviewsPanel";
import RoomPopup from "components/Screens/Marketplace/Customer/Shortlist/RoomPopup";
import TalentAvatar from "components/Screens/Marketplace/Customer/Shortlist/TalentAvatar";
import UnavailablePill, { isUnavailable } from "components/Screens/Marketplace/Customer/Shortlist/UnavailablePill";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { getTalentDisplayName } from "components/Screens/Marketplace/Customer/utils/redaction";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import { PLG_FEATURES } from "plg/features";
import { useMeeting } from "plg/meeting/MeetingContext";
import MeetingShortlist from "components/Screens/Marketplace/Customer/Shortlist/MeetingShortlist";
import { plgEntry } from "plg/analytics/usePlgEntryTracking";
import "./Shortlist.scss";
import experienceLabel from "components/Screens/Marketplace/Customer/utils/experienceLabel";
import speaksLabel from "components/Screens/Marketplace/Customer/utils/speaksLabel";
import ToolChips from "components/Screens/Marketplace/Customer/common/ToolChips/ToolChips";
import useCustomerTools from "components/Screens/Marketplace/Customer/utils/useCustomerTools";

const INTERVIEW_SEATS = 3;

function ShortlistPage() {
  // PLG: one CTA, Request a meeting (prototype renderShortlist). Portal customers keep interviews below.
  const meeting = useMeeting();
  const { refresh } = useShortlist();
  // The shortlist is loaded once for the whole app; opening this page reloads it, so a talent who has left the
  // talent network since (e.g. ASSIGNED) or was paused shows as Unavailable without a full page reload.
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (meeting) return <MeetingShortlist meeting={meeting} />;
  return <PortalShortlist />;
}

function PortalShortlist() {
  const navigate = useNavigate();
  const { loading, items, count, cap, remove } = useShortlist();
  const [panel, setPanel] = useState<"room" | "interview" | null>(null);
  // An interview has 3 seats (even with no shortlist limit): fewer than that, ask whether Edge should
  // find the others; otherwise go straight to the request.
  const seats = cap ?? INTERVIEW_SEATS;
  const requestInterview = () => setPanel(count >= seats ? "interview" : "room");
  // eslint-disable-next-line no-underscore-dangle
  const { routes, hasPermission } = useTalentApi();
  // Interview requests, Edge recommend and Interview status are built but hidden for every customer
  // (PRD-3334; QA PO-2), until PLG_FEATURES.interviews is switched on. Request a hire stays.
  const interviews = PLG_FEATURES.interviews;
  const canRequestHire = hasPermission("talentNetwork.request_hire");
  const myTools = useCustomerTools();
  const openProfile = (id: string) => navigate(routes.talentProfile(id));

  return (
    <div className="sl-page">
      <Helmet>
        <title>Edge | Shortlist</title>
      </Helmet>
      {loading && !items.length && <PageLoader />}
      <div className="sl-page__col">
        <div className="sl-page__head">
          <div>
            <h1 className="heading-h2-semibold">Your shortlist</h1>
            <p className="para-body-m-regular color-text-body-secondary">Everyone you would want to meet, in one place.</p>
          </div>
          {interviews && (
            <Link to={routes.interviewStatus} className="sl-link">
              Interview status
            </Link>
          )}
        </div>

        {/* One empty state (QA PO-12): the count card only once someone is shortlisted. */}
        {count > 0 && (
          <ShortlistCountCard
            count={count}
            cap={cap}
            action={
              interviews ? (
                <Button variant="contained" label="Request interview" onClick={requestInterview} />
              ) : (
                canRequestHire && <Button variant="contained" label="Request a hire" onClick={() => navigate(routes.requestAHire)} />
              )
            }
          />
        )}

        {count > 0 ? (
          <div className="sl-list">
            {items.map(({ talent }) => (
              // eslint-disable-next-line no-underscore-dangle
              <div className={`sl-card plg-card${isUnavailable(talent) ? " sl-card--unavailable" : ""}`} key={talent._id}>
                <TalentAvatar talent={talent} />
                {/* Prototype slCard: role, experience and languages (as the talent network card), up to three tools (QA PO-6) */}
                <div className="sl-card__body">
                  <div className="sl-card__name">
                    {getTalentDisplayName(talent)}
                    {isUnavailable(talent) && <UnavailablePill />}
                  </div>
                  {talent.roles?.[0] && <div className="talent-card__role">{talent.roles[0]}</div>}
                  <div className="sl-card__facts">
                    <span><WorkOutlineOutlinedIcon fontSize="inherit" />{experienceLabel(talent.totalExperience)}</span>
                    <span><TranslateOutlinedIcon fontSize="inherit" />{speaksLabel(talent.languagesSpoken)}</span>
                  </div>
                  <ToolChips tools={talent.tools} myTools={myTools} firstName={talent.firstName} />
                </div>
                <div className="sl-card__actions">
                  {/* Left the talent network since (e.g. ASSIGNED): no profile to open, only Remove. */}
                  {!isUnavailable(talent) && (
                    // eslint-disable-next-line no-underscore-dangle
                    <Button variant="contained" color="secondary" size="small" label="View profile" onClick={() => openProfile(talent._id)} />
                  )}
                  {/* eslint-disable-next-line no-underscore-dangle */}
                  <button type="button" className="sl-remove" onClick={() => remove(talent._id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sl-empty">
            <h3>No one shortlisted yet</h3>
            <p>Shortlist anyone you would want to meet from the talent network.</p>
            <Button variant="contained" label="Browse talent" onClick={() => navigate(routes.talentNetwork, { state: plgEntry("empty_state") })} />
          </div>
        )}
      </div>

      {panel === "room" && (
        <RoomPopup
          items={items}
          seats={seats}
          onFind={() => setPanel("interview")}
          onPick={() => {
            setPanel(null);
            navigate(routes.talentNetwork);
          }}
          onClose={() => setPanel(null)}
        />
      )}
      <RequestInterviewsPanel open={panel === "interview"} onClose={() => setPanel(null)} />
    </div>
  );
}

export default ShortlistPage;
